
import React, { useEffect, useRef, useState } from "react";
import LoadingWheel from "../../components/Shared/LoadingWheel";

import { withAgency } from "../../data/withAgency";
import useWebsiteReports from "../../data/useWebsiteReports";
import WebsiteAnalyticsGraph from "../../components/Analytics/Website/WebsiteAnalyticsGraph";


function _WebsiteAnalytics({ agencyReady, agency }) {
  const { websiteStats } = useWebsiteReports(agency?.id);
  const [isLoading, setIsLoading] = useState(true);
  const [isTotal, setIsTotal] = useState(false);
  const [sourceData, setSourceData] = useState([]);
  const [sourceLimit, setSourceLimit] = useState(15);
  const [pagesLimit, setPagesLimit] = useState(15);
  const [selectedDays, setSelectedDays] = useState("30");

  // Map selectedDays to period type: 7 days = 0, 30 days = 1, 90 days = 2
  const getPeriodType = (days) => {
    switch (days) {
      case "7": return 0;
      case "30": return 1;
      case "90": return 2;
      default: return 1;
    }
  };

  const currentPeriodType = getPeriodType(selectedDays);
  
  // Helper function to get the correct property suffix based on selected days
  const getStatsProperty = (baseName) => {
    return `${baseName}${selectedDays}Days`;
  };

  const pagesTabs = [
    { name: "Top Pages", type: 0 },
    { name: "Entry Pages", type: 1 },
    { name: "Exit Pages", type: 2 },
  ];

  const [selectedPagesTab, setSelectedPagesTab] = useState(pagesTabs[0]);

  const sourceTabs = [
    { name: "Top Sources", key: "topSources", var: "source" },
    { name: "Devices", key: "devices", var: "device" },
    { name: "Channels", key: "channels", var: "channel" },
  ];

  const [selectedSourceTab, setSelectedSourceTab] = useState(sourceTabs[0]);

  const getLabel = (item) => {
    switch (selectedSourceTab.key) {
      case "topSources":
        return item.source === "Direct / None" ? "Direct" : item.source;
      case "devices":
        return item.device;
      case "channels":
        return item.channel;
      default:
        return "";
    }
  };



  useEffect(() => {
    if (agencyReady && !websiteStats.isLoading) {
      if (websiteStats.data) {
        // console.log(websiteStats.data)
        setSourceData(websiteStats.data[selectedSourceTab.key]);
      }
      // Stop loading even with no data so we show the empty state
      // instead of spinning forever.
      setIsLoading(false);
    }
  }, [agency, agencyReady, websiteStats.data, websiteStats.isLoading, isTotal, selectedSourceTab, currentPeriodType]);

  
  return (
    <div className="px-12">
      <div className="sm:flex sm:items-center justify-between mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Website Analytics</h1>
          <p className="mt-2 text-sm text-gray-700">
            Details about visitor numbers, page views, sources and more.
          </p>
        </div>
        <div>
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(e.target.value)}
            className="block w-full rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-sm focus:border-etpink-600 focus:outline-none focus:ring-etpink-600"
          >
            <option value="7">7 Days</option>
            <option value="30">30 Days</option>
            <option value="90">90 Days</option>
          </select>
        </div>
      </div>

      {!isLoading && !websiteStats.data ? (
        <div className="mt-4 py-12 flex flex-col justify-center items-center bg-white rounded-lg shadow">
          <p className="font-medium text-gray-700">
            No website analytics available yet
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Stats will appear here once analytics are connected for this
            agency.
          </p>
        </div>
      ) : (
        <>
      {isLoading ? (
        <div className="py-2 col-span-1 lg:col-span-4 flex justify-center items-center h-40 bg-white rounded-lg shadow">
          <LoadingWheel width="w-12" height="h-12" />
        </div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-8">
            <div className="p-6 col-span-3 lg:col-span-2 bg-white rounded-lg shadow">
              <h1 className="text-base font-normal text-gray-800 lg:pt-2">Website Stats - Last {selectedDays} Days</h1>
              <hr className="" />
              <div className="mt-6">
                <WebsiteAnalyticsGraph stats={websiteStats.data} period={parseInt(selectedDays)} />
              </div>
            </div>

            <div className="p-6 col-span-3 lg:col-span-1 bg-white rounded-lg shadow">
              <div className="mb-4">
                <h1 className="mx-4 text-base font-normal text-gray-800 lg:pt-2">Average Website Stats - Last {selectedDays} Days</h1>
                <hr className="mx-4" />
              </div>

              <div className="divide-y space-y-4">

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Total Visitors
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('visitors')].toLocaleString()}
                  </dd>
                </div>

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Total Visits
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('visits')].toLocaleString()}
                  </dd>
                </div>

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Total Page Views
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('pageViews')].toLocaleString()}
                  </dd>
                </div>

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Views Per Visit
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('viewsPerVisit')].toFixed(2)}
                  </dd>
                </div>

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Bounce Rate (%)
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('bounceRate')]}
                  </dd>
                </div>

                <div className="pt-2 flex flex-col items-center px-4">
                  <dt className="font-medium text-gray-700 truncate">
                    Visit Duration (Seconds)
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-etpink-600">
                    {websiteStats.data[getStatsProperty('visitDuration')]}
                  </dd>
                </div>

              </div>

            </div>

          </div>
        </>
      )}

      {isLoading ? (
        <div className="py-2 mt-8 col-span-1 lg:col-span-4 flex justify-center items-center h-40 bg-white rounded-lg shadow">
          <LoadingWheel width="w-12" height="h-12" />
        </div>
      ) : (
        <>

          <div className="mt-8 grid grid-cols-2 gap-8">
            <div className="p-6 col-span-2 lg:col-span-1 bg-white rounded-lg shadow">
              <div className="flex justify-between items-start">
                <h1 className="text-base font-normal text-gray-800 lg:pt-2">
                  {selectedSourceTab.name} – Last {selectedDays} Days
                </h1>
                <div className="flex space-x-2">
                  {sourceTabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setSelectedSourceTab(tab);
                        setSourceLimit(10);
                      }}
                      className={`text-sm px-2 py-1 ${selectedSourceTab.key === tab.key
                        ? "text-etpink-600 border-b border-b-etpink-600"
                        : "text-gray-500 hover:text-etpink-600"
                        }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </div>
              </div>
              <hr className="mt-2" />

              <div className="mt-4 divide-y">
                {sourceData.filter(x => x.period === currentPeriodType).slice(0, sourceLimit).map((item, index) => (
                  <div key={index} className="flex justify-between pt-2">
                    <dt className="font-medium text-gray-700 truncate">{getLabel(item)}</dt>
                    <dd className="mt-1 text-xl font-semibold text-etpink-600">
                      {item.visitors.toLocaleString()}
                    </dd>
                  </div>
                ))}

                {sourceData.filter(x => x.period === currentPeriodType).length > sourceLimit && (
                  <div className="pt-4 text-center">
                    <button
                      onClick={() => setSourceLimit((prev) => prev + 15)}
                      className="text-sm font-medium text-etpink-600 hover:underline"
                    >
                      View more
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 col-span-2 lg:col-span-1 bg-white rounded-lg shadow">
              <div className="flex justify-between items-start">
                <h1 className="text-base font-normal text-gray-800 lg:pt-2">
                  {selectedPagesTab.name} – Last {selectedDays} Days
                </h1>
                <div className="flex space-x-2">
                  {pagesTabs.map((tab) => (
                    <button
                      key={tab.type}
                      onClick={() => {
                        setSelectedPagesTab(tab);
                        setPagesLimit(10);
                      }}
                      className={`text-sm px-2 py-1 ${selectedPagesTab.type === tab.type
                        ? "text-etpink-600 border-b border-b-etpink-600"
                        : "text-gray-500 hover:text-etpink-600"
                        }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </div>
              </div>
              <hr className="mt-2" />

              <div className="mt-4 divide-y">
                {websiteStats.data.topPages
                  .filter(x => x.pageType === selectedPagesTab.type && x.period === currentPeriodType)
                  .slice(0, pagesLimit)
                  .map((page, index) => (
                    <div key={index} className="flex justify-between pt-2">
                      <dt className="font-medium text-gray-700 truncate">
                        <a href={`${agency.domain}/${page.page}`} target="_blank">{page.page === "/" ? "home" : page.page}</a>
                      </dt>
                      <dd className="mt-1 text-xl font-semibold text-etpink-600">
                        {page.visitors.toLocaleString()}
                      </dd>
                    </div>
                  ))}

                {websiteStats.data.topPages.filter((x) => x.pageType === selectedPagesTab.type && x.period === currentPeriodType).length > pagesLimit && (
                    <div className="pt-4 text-center">
                      <button
                        onClick={() => setPagesLimit((prev) => prev + 15)}
                        className="text-sm font-medium text-etpink-600 hover:underline"
                      >
                        View more
                      </button>
                    </div>
                  )}
              </div>
            </div>

          </div>
        </>
      )}
        </>
      )}

    </div>
  )

}


export const WebsiteAnalytics = withAgency(_WebsiteAnalytics);